import { useEffect, useMemo } from 'react'
import { combineSearchParams, getTypeKey } from 'utils'
import { ApiResponse } from './useApi'
import { ProjectQueryParameter } from 'pages/Home/HomePage.d'
import { ProjectsListParams } from '@contracts'
import usePermissions from './usePermissions'
import useInitialiser from './useInitialiser'

interface UseDependantApiProps {
  apiMethods: ApiResponse<any>
  parentApiSearchParams: URLSearchParams
  defaultQueryParams: ProjectQueryParameter
  hasPreselectionCompleted: boolean
  areReducerDependenciesResolved: boolean
  shouldApplyFilter?: boolean
}

export default function useDependantApi({
  apiMethods,
  parentApiSearchParams,
  defaultQueryParams,
  hasPreselectionCompleted,
  areReducerDependenciesResolved,
  shouldApplyFilter = true,
}: UseDependantApiProps) {
  const {
    fetchData: getProjectsData,
    isLoading: isGetProjectsLoading,
    url: getProjectsUrl,
    setUrl: setGetProjectsUrl,
  } = apiMethods

  const { isInitialising } = useInitialiser({})

  const { clientsWithPermissions } = usePermissions({
    permissionSubset: ['PDP_APP_GET_PROJECTS'],
  })

  const [clientsWithGetProjects] = clientsWithPermissions

  // create the query parameters from filter reducers
  const projectsApiSearchParams: URLSearchParams = useMemo(() => {
    const queryParams = new URLSearchParams(defaultQueryParams)
    return combineSearchParams(parentApiSearchParams, queryParams)
  }, [parentApiSearchParams, hasPreselectionCompleted, areReducerDependenciesResolved])

  // update api url with the query parameters from the reducers
  useEffect(() => {
    const newUrl = new URL(getProjectsUrl)
    newUrl.search = projectsApiSearchParams.toString()
    if (!hasPreselectionCompleted || !areReducerDependenciesResolved) return

    // shouldApplyFilter is true remove sort_by and sort_order from the url
    if (!shouldApplyFilter) {
      newUrl.searchParams.delete('sort_by')
      newUrl.searchParams.delete('sort_order')
    }
    setGetProjectsUrl(newUrl)
  }, [projectsApiSearchParams])

  // Fetch data
  useEffect(() => {
    if (
      isInitialising ||
      !clientsWithGetProjects?.length ||
      isGetProjectsLoading ||
      !getProjectsUrl.searchParams.get('position') ||
      !areReducerDependenciesResolved
    )
      return
    const client_ids = projectsApiSearchParams.get(getTypeKey<ProjectsListParams>('clients'))
    const requestOptions: any = {
      method: 'GET',
      headers: {
        'x-client-ids': client_ids || null,
      },
    }
    if (!client_ids) delete requestOptions.headers['x-client-ids']
    getProjectsData(getProjectsUrl, requestOptions as RequestInit)
  }, [getProjectsUrl.searchParams, isInitialising])

  return getProjectsUrl
}
